// src/pages/Agents/agentMode.ts — which conversation mode an agent is in, and
// which field names that mode reads and writes.
//
// The mode is NOT `agent_type`. `agent_type` is the outer voicex config field
// and the backend defaults it to the literal string "other"
// (AiAgentService.buildAgentConfig: c.put("agent_type", or(b.get("agent_type"), "other"))),
// so a live voice-to-voice agent carries agent_type='other' and reading the
// mode from it renders a realtime agent as a pipeline one.
//
// What actually decides the shape of the document pushed to the runtime is
// AiAgentService.isRealtimeFlow:
//
//     str(or(b.get("agent_flow_type"), b.get("llm_agent_type"))).contains("realtime")
//
// so this resolves in exactly that order — agent_flow_type, then
// llm_agent_type — and only then falls back to agent_type, which older agents
// created by the cpass wizard used as their only mode marker.
import { AiAgent } from 'api/services/aiAgents.service';

export type AgentMode = 'realtime' | 'pipeline' | 'unknown';

/** The realtime value production agents carry in both flow fields. */
export const REALTIME_FLOW = 'gemini_realtime';
/** The backend's default llm_agent.agent_type for the STT→LLM→TTS pipeline. */
export const PIPELINE_AGENT_TYPE = 'simple_llm_agent';

/** Values that positively identify a pipeline agent. */
const PIPELINE_MARKS = [PIPELINE_AGENT_TYPE, 'streaming', 'non_streaming'];

const norm = (v: unknown): string => (v === null || v === undefined ? '' : String(v).trim().toLowerCase());

const isRealtimeMark = (v: string) => v.includes('realtime');

/**
 * The mode this agent is really in.
 *
 * Returns 'unknown' rather than guessing when no field says: an agent whose
 * only marker is agent_type='other' is not a pipeline agent, it is an agent
 * that never declared a mode, and showing "Pipeline" for it is the bug this
 * module exists to stop.
 */
export function resolveAgentMode(a: Partial<AiAgent> | null | undefined): AgentMode {
  if (!a) return 'unknown';
  const marks = [a.agent_flow_type, a.llm_agent_type, a.agent_type].map(norm);
  for (const m of marks) {
    if (!m) continue;                       // an unset field decides nothing
    if (isRealtimeMark(m)) return 'realtime';
    if (PIPELINE_MARKS.includes(m)) return 'pipeline';
    return 'unknown';                       // set, but to something we don't model
  }
  return 'unknown';
}

/**
 * The fields to write when someone picks a mode.
 *
 * Both flow fields are set for realtime because that is the shape every working
 * realtime agent has, and because isRealtimeFlow reads agent_flow_type FIRST —
 * setting only llm_agent_type would leave a stale 'streaming' in front of it
 * and the backend would still build a pipeline document.
 *
 * The outer `agent_type` is deliberately NOT written: it is a different voicex
 * field, and whatever the agent already has there is preserved.
 */
export function agentModePatch(a: Partial<AiAgent>, mode: 'realtime' | 'pipeline'): Partial<AiAgent> {
  if (mode === 'realtime') {
    return { agent_flow_type: REALTIME_FLOW, llm_agent_type: REALTIME_FLOW };
  }
  const patch: Partial<AiAgent> = { llm_agent_type: PIPELINE_AGENT_TYPE };
  // A leftover realtime agent_flow_type outranks llm_agent_type, so it has to
  // go or the agent stays realtime while the control says pipeline.
  if (isRealtimeMark(norm(a.agent_flow_type))) patch.agent_flow_type = 'streaming';
  return patch;
}

// ── Voice and language ────────────────────────────────────────────────────
// These are two different pairs of fields, not two spellings of one pair. A
// realtime model takes its voice and language from llm_config
// (voice_name / language_code); a pipeline agent takes them from the
// synthesizer and transcriber blocks, which the backend fills from
// `voice`/`tts_voice_id` and `language`. Writing one where the other is read
// is why edits to a realtime agent appeared to save and changed nothing.

export const voiceField = (mode: AgentMode): 'voice_name' | 'voice' =>
  mode === 'realtime' ? 'voice_name' : 'voice';

export const languageField = (mode: AgentMode): 'language_code' | 'language' =>
  mode === 'realtime' ? 'language_code' : 'language';

/** Canonical name for the mode first, then the other one, so nothing renders blank. */
export function agentVoice(a: Partial<AiAgent>, mode: AgentMode): string {
  const v = mode === 'realtime' ? (a.voice_name ?? a.voice) : (a.voice ?? a.voice_name);
  return v ?? '';
}

export function agentLanguage(a: Partial<AiAgent>, mode: AgentMode): string {
  const v = mode === 'realtime' ? (a.language_code ?? a.language) : (a.language ?? a.language_code);
  return v ?? '';
}

/**
 * Whether this agent's runtime document is its own rather than one cpass
 * builds. cpass lays only a short list of fields onto an adopted document
 * (AiAgentService.overlayOnto) — name, greeting, status, prompt, api tools and
 * the task_config timings — so model, voice, language and mode are recorded in
 * cpass but never reach the runtime for these agents.
 */
export const isAdopted = (a: Partial<AiAgent> | null | undefined): boolean =>
  !!a && (a.adopted_from_runtime === true || !!a.runtime_document);
